import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { formatDate } from '../utils/helpers';
import {
  ArrowLeft, User, Mail, Calendar, Key, Shield, Upload,
  Camera, ExternalLink, Check, AlertCircle
} from 'lucide-react';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef(null);

  const [profile, setProfile] = useState(null);
  const [profilePictureUrl, setProfilePictureUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [error, setError] = useState(null);

  const authentikAccountUrl = process.env.REACT_APP_AUTHENTIK_ACCOUNT_URL ||
    'https://auth.vlevente.me/if/user/#/settings';

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const response = await api.get('/auth/me');
      setProfile(response.data);

      // Load profile picture if exists
      if (response.data.has_profile_picture) {
        loadProfilePicture(response.data.id);
      }
    } catch (err) {
      console.error('Load profile error:', err);
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const loadProfilePicture = async (userId) => {
    try {
      const response = await api.get(`/auth/profile-picture/${userId}`, {
        responseType: 'blob'
      });
      const url = URL.createObjectURL(response.data);
      setProfilePictureUrl(url);
    } catch (err) {
      console.error('Load profile picture error:', err);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Only image files can be uploaded');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be maximum 5MB');
      return;
    }

    try {
      setUploading(true);
      setError(null);

      // Convert to base64
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result.split(',')[1];

        try {
          await api.put('/auth/profile-picture', {
            picture: base64,
            mimeType: file.type
          });

          setUploadSuccess(true);
          setTimeout(() => setUploadSuccess(false), 3000);
          loadProfile();
        } catch (err) {
          console.error('Upload error:', err);
          setError('Failed to upload image');
        } finally {
          setUploading(false);
        }
      };

      reader.readAsDataURL(file);
    } catch (err) {
      console.error('File read error:', err);
      setError('Error reading file');
      setUploading(false);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Cleanup blob URL when component unmounts
  useEffect(() => {
    return () => {
      if (profilePictureUrl) {
        URL.revokeObjectURL(profilePictureUrl);
      }
    };
  }, [profilePictureUrl]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Header />

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back button */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-6"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to projects
        </button>

        <div className="card p-6 lg:p-8">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            Profile settings
          </h1>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner"></div>
            </div>
          ) : error && !profile ? (
            <div className="text-center py-12">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">{error}</p>
            </div>
          ) : (
            <>
              {/* Profile Picture Section */}
              <div className="flex flex-col sm:flex-row items-center gap-6 pb-6 border-b border-gray-200 dark:border-gray-700">
                <div className="relative group">
                  <div className="w-32 h-32 rounded-full overflow-hidden bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                    {profilePictureUrl ? (
                      <img
                        src={profilePictureUrl}
                        alt={profile.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <User className="w-16 h-16 text-primary-600 dark:text-primary-400" />
                    )}
                  </div>

                  {/* Upload overlay */}
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                  >
                    {uploading ? (
                      <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <Camera className="w-8 h-8 text-white" />
                    )}
                  </button>

                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept="image/*"
                    className="hidden"
                  />
                </div>

                <div className="text-center sm:text-left">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    {profile?.name}
                  </h2>
                  <p className="text-gray-500 dark:text-gray-400 mt-1">
                    {profile?.email}
                  </p>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="mt-3 btn btn-secondary btn-sm flex items-center gap-2"
                  >
                    <Upload className="w-4 h-4" />
                    {uploading ? 'Uploading...' : 'Change profile picture'}
                  </button>
                </div>
              </div>

              {/* Success/Error Messages */}
              {uploadSuccess && (
                <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-lg flex items-center gap-2">
                  <Check className="w-5 h-5" />
                  Profile picture successfully updated
                </div>
              )}

              {error && profile && (
                <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  {error}
                </div>
              )}

              {/* Profile Info */}
              <div className="mt-6 space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Account information
                </h3>

                <div className="grid gap-4">
                  {/* Name */}
                  <div className="flex items-start gap-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                    <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                      <User className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Name</p>
                      <p className="font-medium text-gray-900 dark:text-white">{profile?.name}</p>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex items-start gap-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                    <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                      <Mail className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Email address</p>
                      <p className="font-medium text-gray-900 dark:text-white">{profile?.email}</p>
                    </div>
                  </div>

                  {/* OIDC Subject */}
                  <div className="flex items-start gap-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                    <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                      <Shield className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Authentik ID</p>
                      <p className="font-medium text-gray-900 dark:text-white font-mono text-sm">
                        {profile?.oidc_subject}
                      </p>
                    </div>
                  </div>

                  {/* Created At */}
                  <div className="flex items-start gap-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                    <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                      <Calendar className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Registration date</p>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {profile?.created_at ? formatDate(profile.created_at) : '-'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Password Change Section */}
              <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-start gap-4 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                  <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                    <Key className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-amber-800 dark:text-amber-200">
                      Change password
                    </h4>
                    <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                      You can change your password on the central Authentik authentication server.
                      Click the button below to open account settings.
                    </p>
                    <a
                      href={authentikAccountUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors text-sm font-medium"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Authentik account settings
                    </a>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default ProfilePage;
